import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { IndicatorBar } from '../../../../components/IndicatorBar'
import { TestParameterPickerProps } from '../../../../types/TestParameter'

export const TestParameterPicker = ({
  parameter,
  selectedValue,
  onValueSelect,
  index,
  flagParameter,
}: TestParameterPickerProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [canScroll, setCanScroll] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkScroll = () => {
      if (scrollRef.current) {
        const { scrollWidth, clientWidth } = scrollRef.current
        setCanScroll(scrollWidth > clientWidth)
      }
    }

    checkScroll()
    window.addEventListener('resize', checkScroll)
    return () => window.removeEventListener('resize', checkScroll)
  }, [])

  return (
    <Box
      id={`parameter-${index + 1}`}
      sx={{
        p: { xs: 2, md: 3 },
        pl: { xs: 0, md: 1 },
        pt: 0,
        width: '100%',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Typography
          fontSize={{ xs: '13px', md: '14px' }}
          fontWeight={600}
          color="text.primary"
          mb={2}
          textAlign="left"
        >
          {index + 1} {parameter.name} {parameter.unit}
        </Typography>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            '&::after':
              isMobile && canScroll
                ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    height: '100%',
                    width: '40px',
                    background:
                      'linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0))',
                    pointerEvents: 'none',
                  }
                : {},
          }}
        >
          <Stack
            ref={scrollRef}
            direction="row"
            spacing={1}
            sx={{
              maxWidth: '100%',
              overflowX: 'auto',
              pb: 1,
              pr: canScroll ? 4 : 0,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {flagParameter && <IndicatorBar height={130} />}
            {parameter.values.map((value, idx) => (
              <Box
                key={idx}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: selectedValue === value.value ? '#00598E' : '#E2E8F0',
                  backgroundColor: selectedValue === value.value ? '#E6EEF4' : 'white',
                  p: { xs: 0.75, md: 1 },
                  minWidth: { xs: 45, md: 50 },
                  flexShrink: 0,
                }}
                onClick={() => onValueSelect(value.value)}
              >
                <Stack alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: { xs: 16, md: 20 },
                      height: { xs: 16, md: 20 },
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: selectedValue === value.value ? '#00598E' : '#94A3B8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {selectedValue === value.value && (
                      <Box
                        sx={{
                          width: { xs: 8, md: 10 },
                          height: { xs: 8, md: 10 },
                          borderRadius: '50%',
                          backgroundColor: '#00598E',
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    fontSize={{ xs: '12px', md: '14px' }}
                    color="text.primary"
                    fontWeight={selectedValue === value.value ? 600 : 400}
                  >
                    {value.value}
                  </Typography>
                  <Box
                    sx={{
                      width: { xs: 40, md: 48 },
                      height: { xs: 40, md: 48 },
                      backgroundColor: value.color,
                      borderRadius: 1,
                    }}
                  />
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}
